import React, { useState, useEffect } from 'react';
import {
  DollarSign, Calculator, CheckCircle, Clock, Filter,
  ChevronDown, Plus, Trash2, Download
} from 'lucide-react';
import { downloadCSV } from '../utils/csvExport';
import { supabase } from '../supabase';
import {
  formatCurrency, formatDate, getMonthLabel, getCurrentPeriodMonth,
  getLastNMonths, getMonthlyRevenue, calculateCommission
} from '../utils/commissionCalc';
import { logAdminAction } from '../utils/auditLog';

export default function Commissions() {
  const [commissions, setCommissions] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentPeriodMonth());
  const [filterAffiliate, setFilterAffiliate] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showTestModal, setShowTestModal] = useState(false);
  const [testForm, setTestForm] = useState({
    affiliate_id: '',
    company_name: 'Test Company',
    commission_amount: '5.00'
  });

  const months = getLastNMonths(12);

  useEffect(() => {
    loadData();
  }, [selectedMonth]);

  const loadData = async () => {
    try {
      // Load commissions for selected month
      const { data: commissionsData } = await supabase
        .from('affiliate_commissions')
        .select(`
          *,
          affiliates (id, name, code),
          companies (id, name, tier, subscription_status)
        `)
        .eq('period_month', selectedMonth)
        .order('created_at', { ascending: false });

      // Load all affiliates
      const { data: affiliatesData } = await supabase
        .from('affiliates')
        .select('*')
        .eq('active', true)
        .order('name');

      setCommissions(commissionsData || []);
      setAffiliates(affiliatesData || []);
    } catch (error) {
      console.error('Error loading commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthlyCommissions = async () => {
    if (!window.confirm(`Calculate commissions for ${getMonthLabel(selectedMonth)}? This will create/update commission records for all attributed accounts with active subscriptions.`)) {
      return;
    }

    setCalculating(true);

    try {
      // Get all attributed companies with active subscriptions
      const { data: companies } = await supabase
        .from('companies')
        .select(`
          id, name, tier, billing_interval, subscription_status,
          referred_by_affiliate_id, referral_captured_at, created_at
        `)
        .not('referred_by_affiliate_id', 'is', null)
        .eq('subscription_status', 'active');

      // Get affiliates for commission calculation
      const { data: affiliatesData } = await supabase
        .from('affiliates')
        .select('*')
        .eq('active', true);

      const affiliateMap = {};
      (affiliatesData || []).forEach(a => {
        affiliateMap[a.id] = a;
      });

      let created = 0;
      let updated = 0;

      let dealBonuses = 0;

      for (const company of companies || []) {
        const affiliate = affiliateMap[company.referred_by_affiliate_id];
        if (!affiliate) continue;

        // Check commission duration — skip if expired
        if (affiliate.commission_duration_months) {
          const referralDate = new Date(company.referral_captured_at || company.created_at);
          const selectedDate = new Date(selectedMonth);
          const monthsSince = (selectedDate.getFullYear() - referralDate.getFullYear()) * 12 +
            (selectedDate.getMonth() - referralDate.getMonth());
          if (monthsSince >= affiliate.commission_duration_months) continue;
        }

        const monthlyRevenue = getMonthlyRevenue(company.tier, company.billing_interval);
        const commissionAmount = calculateCommission(affiliate, monthlyRevenue);

        // Check if recurring commission already exists
        const { data: existing } = await supabase
          .from('affiliate_commissions')
          .select('id, status')
          .eq('affiliate_id', affiliate.id)
          .eq('company_id', company.id)
          .eq('period_month', selectedMonth)
          .eq('commission_type', 'recurring')
          .single();

        if (existing) {
          // Only update if not already paid
          if (existing.status !== 'paid') {
            await supabase
              .from('affiliate_commissions')
              .update({
                company_revenue: monthlyRevenue,
                commission_amount: commissionAmount
              })
              .eq('id', existing.id);
            updated++;
          }
        } else {
          // Create new commission record
          await supabase.from('affiliate_commissions').insert({
            affiliate_id: affiliate.id,
            company_id: company.id,
            period_month: selectedMonth,
            company_revenue: monthlyRevenue,
            commission_amount: commissionAmount,
            commission_type: 'recurring',
            status: 'owed'
          });
          created++;
        }

        // Deal bonus — one-time per company
        if (affiliate.deal_bonus_amount > 0) {
          const { data: existingBonus } = await supabase
            .from('affiliate_commissions')
            .select('id')
            .eq('affiliate_id', affiliate.id)
            .eq('company_id', company.id)
            .eq('commission_type', 'deal_bonus')
            .single();

          if (!existingBonus) {
            await supabase.from('affiliate_commissions').insert({
              affiliate_id: affiliate.id,
              company_id: company.id,
              period_month: selectedMonth,
              company_revenue: 0,
              commission_amount: affiliate.deal_bonus_amount,
              commission_type: 'deal_bonus',
              status: 'owed'
            });
            dealBonuses++;
          }
        }
      }

      // SPIF payouts — third pass
      let spifPayouts = 0;
      const selectedDate = new Date(selectedMonth);
      const { data: activeSpifs } = await supabase
        .from('affiliate_spifs')
        .select('*')
        .eq('active', true)
        .lte('start_date', selectedMonth)
        .gte('end_date', selectedMonth);

      if (activeSpifs && activeSpifs.length > 0) {
        for (const spif of activeSpifs) {
          // Find qualifying affiliates
          const qualifyingAffiliates = (affiliatesData || []).filter(a => {
            const tiersEmpty = !spif.target_tiers || spif.target_tiers.length === 0;
            const idsEmpty = !spif.target_affiliate_ids || spif.target_affiliate_ids.length === 0;
            if (tiersEmpty && idsEmpty) return true;
            return spif.target_tiers?.includes(a.tier) || spif.target_affiliate_ids?.includes(a.id);
          });

          for (const aff of qualifyingAffiliates) {
            // Check if this affiliate had any signups this period
            const affCompanies = (companies || []).filter(c => c.referred_by_affiliate_id === aff.id);
            if (affCompanies.length === 0 && spif.bonus_type === 'per_signup') continue;

            if (spif.bonus_type === 'per_signup') {
              // One payout per signup in the period
              for (const comp of affCompanies) {
                const { data: existingPayout } = await supabase
                  .from('affiliate_spif_payouts')
                  .select('id')
                  .eq('spif_id', spif.id)
                  .eq('affiliate_id', aff.id)
                  .eq('company_id', comp.id)
                  .single();

                if (!existingPayout) {
                  await supabase.from('affiliate_spif_payouts').insert({
                    spif_id: spif.id,
                    affiliate_id: aff.id,
                    company_id: comp.id,
                    amount: spif.bonus_amount,
                    status: 'owed'
                  });
                  spifPayouts++;
                }
              }
            } else {
              // flat or percentage: one payout per affiliate per spif
              const { data: existingPayout } = await supabase
                .from('affiliate_spif_payouts')
                .select('id')
                .eq('spif_id', spif.id)
                .eq('affiliate_id', aff.id)
                .single();

              if (!existingPayout) {
                await supabase.from('affiliate_spif_payouts').insert({
                  spif_id: spif.id,
                  affiliate_id: aff.id,
                  amount: spif.bonus_amount,
                  status: 'owed'
                });
                spifPayouts++;
              }
            }
          }
        }
      }

      const parts = [`${created} created, ${updated} updated`];
      if (dealBonuses > 0) parts.push(`${dealBonuses} deal bonuses`);
      if (spifPayouts > 0) parts.push(`${spifPayouts} SPIF payouts`);
      alert(`Commissions calculated!\n${parts.join(', ')}.`);
      loadData();
    } catch (error) {
      alert('Error calculating commissions: ' + error.message);
    } finally {
      setCalculating(false);
    }
  };

  const markAsPaid = async (commission) => {
    if (!window.confirm(`Mark ${commission.affiliates?.name}'s commission of ${formatCurrency(commission.commission_amount)} as paid?`)) {
      return;
    }

    try {
      await supabase
        .from('affiliate_commissions')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString()
        })
        .eq('id', commission.id);

      const adminEmail = (() => { try { return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email; } catch { return 'unknown'; } })();
      await logAdminAction(adminEmail, 'commission_paid', 'commission', commission.id, commission.affiliates?.name, { amount: commission.commission_amount, period: selectedMonth });
      loadData();
    } catch (error) {
      alert('Error marking as paid: ' + error.message);
    }
  };

  const markAllAsPaid = async () => {
    const owedCommissions = filteredCommissions.filter(c => c.status === 'owed');
    if (owedCommissions.length === 0) {
      alert('No pending commissions to mark as paid.');
      return;
    }

    if (!window.confirm(`Mark all ${owedCommissions.length} pending commissions as paid?`)) {
      return;
    }

    try {
      await supabase
        .from('affiliate_commissions')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString()
        })
        .in('id', owedCommissions.map(c => c.id));

      const adminEmail = (() => { try { return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email; } catch { return 'unknown'; } })();
      const totalAmount = owedCommissions.reduce((sum, c) => sum + parseFloat(c.commission_amount), 0);
      await logAdminAction(adminEmail, 'commissions_bulk_paid', 'commission', null, `${owedCommissions.length} commissions`, { count: owedCommissions.length, total_amount: totalAmount, period: selectedMonth });
      loadData();
    } catch (error) {
      alert('Error marking commissions as paid: ' + error.message);
    }
  };

  const deleteCommission = async (commission) => {
    if (!window.confirm('Delete this commission record?')) return;

    try {
      await supabase.from('affiliate_commissions').delete().eq('id', commission.id);
      loadData();
    } catch (error) {
      alert('Error deleting commission: ' + error.message);
    }
  };

  const createTestCommission = async () => {
    if (!testForm.affiliate_id) {
      alert('Please select an affiliate');
      return;
    }

    try {
      await supabase.from('affiliate_commissions').insert({
        affiliate_id: testForm.affiliate_id,
        company_id: null, // Test commission without real company
        period_month: selectedMonth,
        company_revenue: 0,
        commission_amount: parseFloat(testForm.commission_amount),
        status: 'owed',
        is_test: true,
        notes: `Test commission for ${testForm.company_name}`
      });

      setShowTestModal(false);
      setTestForm({ affiliate_id: '', company_name: 'Test Company', commission_amount: '5.00' });
      loadData();
    } catch (error) {
      alert('Error creating test commission: ' + error.message);
    }
  };

  // Filter commissions
  const filteredCommissions = commissions.filter(comm => {
    const matchesAffiliate = filterAffiliate === 'all' || comm.affiliate_id === filterAffiliate;
    const matchesStatus = filterStatus === 'all' || comm.status === filterStatus;
    return matchesAffiliate && matchesStatus;
  });

  // Calculate totals
  const totals = {
    owed: filteredCommissions.filter(c => c.status === 'owed').reduce((sum, c) => sum + parseFloat(c.commission_amount), 0),
    paid: filteredCommissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + parseFloat(c.commission_amount), 0)
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading commissions...
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 3rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem'
          }}>
            Commissions
          </h1>
          <p style={{ color: '#888' }}>
            Calculate and track affiliate payouts
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => downloadCSV(filteredCommissions, [
              { key: 'affiliates.name', label: 'Affiliate Name' },
              { key: 'companies.name', label: 'Company', format: (v, row) => row.is_test ? (row.notes || 'Test') : (v || 'Unknown') },
              { key: 'companies.tier', label: 'Tier' },
              { key: 'company_revenue', label: 'Monthly Revenue', format: (v) => v ? Number(v).toFixed(2) : '0.00' },
              { key: 'commission_amount', label: 'Commission Amount', format: (v) => v ? Number(v).toFixed(2) : '0.00' },
              { key: 'status', label: 'Status' }
            ], `commissions-${selectedMonth}.csv`)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#e0e0e0',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            <Download size={18} />
            Export CSV
          </button>
          <button
            onClick={() => setShowTestModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#888',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <Plus size={18} />
            Test Commission
          </button>
          <button
            onClick={calculateMonthlyCommissions}
            disabled={calculating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: '#ff6b35',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontWeight: '600',
              cursor: calculating ? 'not-allowed' : 'pointer',
              opacity: calculating ? 0.7 : 1
            }}
          >
            <Calculator size={18} />
            {calculating ? 'Calculating...' : 'Calculate Commissions'}
          </button>
        </div>
      </div>

      {/* Not-yet-calculated banner for current month */}
      {selectedMonth === getCurrentPeriodMonth() && commissions.length === 0 && !calculating && (
        <div style={{
          background: '#2a2a1a',
          border: '1px solid #f7931e',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.75rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Clock size={18} color="#f7931e" />
            <span style={{ color: '#e0e0e0' }}>
              <strong>{getMonthLabel(selectedMonth)}</strong> commissions haven't been calculated yet.
            </span>
          </div>
          <button
            onClick={calculateMonthlyCommissions}
            style={{
              padding: '0.5rem 1rem',
              background: '#ff6b35',
              border: 'none',
              borderRadius: '6px',
              color: '#fff',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            Calculate Now
          </button>
        </div>
      )}

      {/* Month Selector & Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            fontWeight: '600',
            minWidth: '180px'
          }}
        >
          {months.map(month => (
            <option key={month} value={month}>{getMonthLabel(month)}</option>
          ))}
        </select>

        <select
          value={filterAffiliate}
          onChange={(e) => setFilterAffiliate(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            minWidth: '150px'
          }}
        >
          <option value="all">All Affiliates</option>
          {affiliates.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            minWidth: '120px'
          }}
        >
          <option value="all">All Status</option>
          <option value="owed">Owed</option>
          <option value="paid">Paid</option>
        </select>

        {totals.owed > 0 && (
          <button
            onClick={markAllAsPaid}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              background: '#4ecca320',
              border: '1px solid #4ecca340',
              borderRadius: '8px',
              color: '#4ecca3',
              fontWeight: '600',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          >
            <CheckCircle size={18} />
            Mark All Paid
          </button>
        )}
      </div>

      {/* Totals */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1.25rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #f39c12'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Clock size={18} color="#f39c12" />
            <span style={{ color: '#888', fontSize: '0.85rem' }}>Owed</span>
          </div>
          <div style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#f39c12',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            {formatCurrency(totals.owed)}
          </div>
        </div>

        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1.25rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #4ecca3'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <CheckCircle size={18} color="#4ecca3" />
            <span style={{ color: '#888', fontSize: '0.85rem' }}>Paid</span>
          </div>
          <div style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#4ecca3',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            {formatCurrency(totals.paid)}
          </div>
        </div>

        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1.25rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #3498db'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <DollarSign size={18} color="#3498db" />
            <span style={{ color: '#888', fontSize: '0.85rem' }}>Total</span>
          </div>
          <div style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#3498db',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            {formatCurrency(totals.owed + totals.paid)}
          </div>
        </div>
      </div>

      {/* Commission List */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        overflow: 'hidden'
      }}>
        {filteredCommissions.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
            <DollarSign size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
            <p>No commissions for {getMonthLabel(selectedMonth)}.</p>
            <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
              Click "Calculate Commissions" to generate records for active accounts.
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Affiliate
                </th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Account
                </th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Revenue
                </th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Commission
                </th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Status
                </th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCommissions.map(commission => (
                <tr
                  key={commission.id}
                  style={{
                    borderBottom: '1px solid #2a2a2a',
                    background: commission.is_test ? '#2a2a1a' : 'transparent'
                  }}
                >
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: '600', color: '#e0e0e0' }}>
                      {commission.affiliates?.name || 'Unknown'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                      {commission.affiliates?.code}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {commission.is_test ? (
                      <div>
                        <span style={{
                          padding: '0.125rem 0.5rem',
                          background: '#f7931e20',
                          color: '#f7931e',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: '600'
                        }}>
                          TEST
                        </span>
                        <div style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                          {commission.notes}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ color: '#e0e0e0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {commission.companies?.name || 'Unknown'}
                          {commission.commission_type === 'deal_bonus' && (
                            <span style={{
                              padding: '0.1rem 0.4rem',
                              background: '#4ecca320',
                              color: '#4ecca3',
                              borderRadius: '4px',
                              fontSize: '0.65rem',
                              fontWeight: '600'
                            }}>
                              DEAL BONUS
                            </span>
                          )}
                          {commission.commission_type === 'spif' && (
                            <span style={{
                              padding: '0.1rem 0.4rem',
                              background: '#9b59b620',
                              color: '#9b59b6',
                              borderRadius: '4px',
                              fontSize: '0.65rem',
                              fontWeight: '600'
                            }}>
                              SPIF
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          {commission.companies?.tier} / {commission.companies?.subscription_status}
                        </div>
                      </div>
                    )}
                  </td>
                  <td style={{
                    padding: '1rem',
                    textAlign: 'right',
                    color: '#888',
                    fontFamily: '"JetBrains Mono", monospace'
                  }}>
                    {commission.company_revenue > 0 ? formatCurrency(commission.company_revenue) : '-'}
                  </td>
                  <td style={{
                    padding: '1rem',
                    textAlign: 'right',
                    fontWeight: '700',
                    color: commission.status === 'paid' ? '#4ecca3' : '#f39c12',
                    fontFamily: '"JetBrains Mono", monospace'
                  }}>
                    {formatCurrency(commission.commission_amount)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      background: commission.status === 'paid' ? '#4ecca320' : '#f39c1220',
                      color: commission.status === 'paid' ? '#4ecca3' : '#f39c12',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {commission.status}
                    </span>
                    {commission.paid_at && (
                      <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                        {formatDate(commission.paid_at)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      {commission.status === 'owed' && (
                        <button
                          onClick={() => markAsPaid(commission)}
                          style={{
                            padding: '0.375rem 0.75rem',
                            background: '#4ecca320',
                            border: 'none',
                            borderRadius: '4px',
                            color: '#4ecca3',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Mark Paid
                        </button>
                      )}
                      <button
                        onClick={() => deleteCommission(commission)}
                        style={{
                          padding: '0.375rem',
                          background: '#2a1a1a',
                          border: 'none',
                          borderRadius: '4px',
                          color: '#e74c3c',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Test Commission Modal */}
      {showTestModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #3a3a3a',
            padding: '2rem',
            maxWidth: '400px',
            width: '100%'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#e0e0e0',
              marginBottom: '1.5rem'
            }}>
              Create Test Commission
            </h2>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0' }}>
                Affiliate *
              </label>
              <select
                value={testForm.affiliate_id}
                onChange={(e) => setTestForm({ ...testForm, affiliate_id: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '6px',
                  color: '#e0e0e0'
                }}
              >
                <option value="">Select affiliate...</option>
                {affiliates.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0' }}>
                Description
              </label>
              <input
                type="text"
                value={testForm.company_name}
                onChange={(e) => setTestForm({ ...testForm, company_name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '6px',
                  color: '#e0e0e0'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0' }}>
                Commission Amount ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={testForm.commission_amount}
                onChange={(e) => setTestForm({ ...testForm, commission_amount: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '6px',
                  color: '#e0e0e0'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowTestModal(false)}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#e0e0e0',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={createTestCommission}
                style={{
                  flex: 2,
                  padding: '0.875rem',
                  background: '#ff6b35',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Create Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
